import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  Lock, Unlock, IndianRupee, Save, RefreshCw, Users, 
  TrendingUp, CreditCard, Eye, EyeOff
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaywallAdmin = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    free_colleges_count: 5,
    price: 50,
    currency: 'INR',
    show_blurred_preview: true,
    payment_methods: ['upi', 'card', 'netbanking'],
    discount_text: ''
  });
  const [stats, setStats] = useState({
    total_payments: 0,
    successful_payments: 0,
    total_revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/paywall/settings`);
      const data = await res.json();
      if (data.success) {
        setSettings(data.settings);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching paywall settings:', error);
      toast({ title: 'Error', description: 'Failed to fetch settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/paywall/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: 'Paywall settings saved' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Paywall Settings</h2>
        <Button variant="outline" size="sm" onClick={fetchSettings}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Total Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-600">{stats.total_payments}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Successful
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{stats.successful_payments}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
              <IndianRupee className="w-4 h-4" /> Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-purple-600">₹{stats.total_revenue}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" /> Paywall Configuration
          </CardTitle>
          <CardDescription>
            Control whether users need to pay to see full college results
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              {settings.enabled ? (
                <Lock className="w-6 h-6 text-amber-500" />
              ) : (
                <Unlock className="w-6 h-6 text-green-500" />
              )}
              <div>
                <Label className="text-lg font-semibold">
                  {settings.enabled ? 'Paywall Enabled' : 'Paywall Disabled'}
                </Label>
                <p className="text-sm text-slate-500">
                  {settings.enabled 
                    ? 'Users must pay to see all colleges' 
                    : 'All colleges are shown for free'}
                </p>
              </div>
            </div>
            <Switch 
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
            />
          </div>

          {settings.enabled && (
            <>
              {/* Price and Free Colleges */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <IndianRupee className="w-4 h-4" /> Price (₹)
                  </Label>
                  <Input 
                    type="number"
                    value={settings.price}
                    onChange={(e) => setSettings({ ...settings, price: parseInt(e.target.value) })}
                    min="1"
                  />
                  <p className="text-xs text-slate-500">Amount users pay to unlock full results</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> Free Colleges to Show
                  </Label>
                  <Input 
                    type="number"
                    value={settings.free_colleges_count}
                    onChange={(e) => setSettings({ ...settings, free_colleges_count: parseInt(e.target.value) })}
                    min="0"
                    max="50"
                  />
                  <p className="text-xs text-slate-500">Number of colleges shown for free before paywall</p>
                </div>
              </div>

              {/* Preview Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {settings.show_blurred_preview ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    <div>
                      <Label>Show Blurred Preview</Label>
                      <p className="text-xs text-slate-500">Show blurred cards for locked colleges</p>
                    </div>
                  </div>
                  <Switch 
                    checked={settings.show_blurred_preview}
                    onCheckedChange={(v) => setSettings({ ...settings, show_blurred_preview: v })}
                  />
                </div>
              </div>

              {/* Discount Text */}
              <div className="space-y-2">
                <Label>Discount/Promo Text (Optional)</Label>
                <Input 
                  value={settings.discount_text}
                  onChange={(e) => setSettings({ ...settings, discount_text: e.target.value })}
                  placeholder="e.g., 50% OFF - Limited Time!"
                />
                <p className="text-xs text-slate-500">Shown on the payment modal as a badge</p>
              </div>
            </>
          )}

          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" /> Saving...
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" /> Save Settings
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setSettings({ ...settings, enabled: false })}
              disabled={!settings.enabled}
            >
              <Unlock className="w-4 h-4 mr-2" /> Disable Paywall
            </Button>
            <Button 
              variant="outline"
              onClick={() => setSettings({ ...settings, enabled: true, price: 50, free_colleges_count: 5 })}
              disabled={settings.enabled}
            >
              <Lock className="w-4 h-4 mr-2" /> Enable Default Paywall (₹50)
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Remember to click "Save Settings" after making changes
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaywallAdmin;
